import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = 'LinkedIn Profile Optimizer – Best AI Tool to Audit, Optimize and Rank Your LinkedIn Profile for Searches | Growleads.io',
  description = 'The most advanced AI-powered LinkedIn Profile Optimizer to help you rank higher in searches, fix hidden issues, and attract better clients, recruiters and opportunities. LinkedIn Profile Optimizer audits your entire LinkedIn profile in minutes and gives clear, personalized recommendations based on proven optimization practices used by top-performing profiles. Instantly improve visibility, credibility and conversions with an AI-driven LinkedIn audit designed for founders, consultants, executives and job seekers.',
  keywords = 'linkedin profile optimizer, linkedin profile optimize, linkedin profile optimize tool, linkedin profile enhance, linkedin profile enhancer, linkedin profile optimization, best linkedin tool, best linkedin profile tool, best ai tools, best linkedin ai tool, ai linkedin profile tool, linkedin profile ai, linkedin profile ai optimizer, ai profile optimizer, linkedin profile review, linkedin profile audit, optimize linkedin profile for leads, linkedin profile optimization service, ai linkedin profile optimizer, ai linkedin profile audit, linkedin profile optimization for founders, linkedin profile optimization for executives, linkedin profile optimization job seekers, linkedin profile optimization free, best ai profile tool',
  author = 'LinkedIn Profile Optimizer',
  canonical = 'https://optimizer.growleads.io',
  image = 'https://i.ibb.co/NgQ4T8Gx/Trans-Optimizer-Icon.png',
  type = 'website',
  twitterCard = 'summary_large_image',
  twitterHandle = '@growleadsio',
  structuredData = null
}) => {
  const siteUrl = 'https://optimizer.growleads.io';
  const fullCanonical = canonical.startsWith('http') ? canonical : `${siteUrl}${canonical}`;
  const fullImage = image.startsWith('http') ? image : `${siteUrl}${image}`;

  // Default structured data (Organization + WebApplication)
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        'name': 'LinkedIn Profile Optimizer',
        'url': siteUrl,
        'logo': {
          '@type': 'ImageObject',
          'url': fullImage,
          'width': 512,
          'height': 512
        },
        'description': 'The most advanced AI-powered LinkedIn Profile Optimizer to help you rank higher in searches, fix hidden issues, and attract better clients, recruiters and opportunities. LinkedIn Profile Optimizer audits your entire LinkedIn profile in minutes and gives clear, personalized recommendations based on proven optimization practices used by top-performing profiles. Instantly improve visibility, credibility and conversions with an AI-driven LinkedIn audit designed for founders, consultants, executives and job seekers.',
        'sameAs': [
          'https://x.com/growleadsio',
          'https://www.linkedin.com/company/linkedinprofileoptimizer/'
        ]
      },
      {
        '@type': 'WebApplication',
        '@id': `${siteUrl}/#webapp`,
        'name': 'LinkedIn Profile Optimizer',
        'url': siteUrl,
        'description': description,
        'applicationCategory': 'BusinessApplication',
        'operatingSystem': 'Web Browser',
        'offers': {
          '@type': 'Offer',
          'price': '0',
          'priceCurrency': 'USD'
        },
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.8',
          'reviewCount': '127'
        }
      },
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        'url': siteUrl,
        'name': 'LinkedIn Profile Optimizer',
        'description': description,
        'publisher': {
          '@id': `${siteUrl}/#organization`
        },
        'potentialAction': {
          '@type': 'SearchAction',
          'target': {
            '@type': 'EntryPoint',
            'urlTemplate': `${siteUrl}/search?q={search_term_string}`
          },
          'query-input': 'required name=search_term_string'
        }
      }
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <link rel="canonical" href={fullCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="LinkedIn Profile Optimizer" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content={twitterCard} />
      <meta property="twitter:url" content={fullCanonical} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullImage} />
      <meta property="twitter:creator" content={twitterHandle} />
      <meta property="twitter:site" content={twitterHandle} />

      {/* Additional SEO Meta Tags */}
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />

      {/* Mobile Meta Tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="LinkedIn Profile Optimizer" />

      {/* Favicon and Icons - Progressive Web App */}
      <link rel="icon" type="image/png" sizes="32x32" href="https://i.ibb.co/NgQ4T8Gx/Trans-Optimizer-Icon.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="https://i.ibb.co/NgQ4T8Gx/Trans-Optimizer-Icon.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="https://i.ibb.co/NgQ4T8Gx/Trans-Optimizer-Icon.png" />
      <meta name="theme-color" content="#0077B5" />
      <meta name="msapplication-TileColor" content="#0077B5" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>

      {/* Preconnect to External Domains for Performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
    </Helmet>
  );
};

export default SEO;
